import React, { useEffect, useState } from 'react';
import DetailSection from './components/DetailSection';

import { Tab } from '@headlessui/react';
import { useSuiClient } from '@mysten/dapp-kit';
import lootboxImage from '../assets/scard/lootbox.png';
import { useKioskClient } from '../context/KioskClientContext';
import { useOwnedKiosk } from '../hooks/kiosk';
import { useCardTrackers } from '../hooks/useCardTrackers';
import { useGeoBlocking } from '../hooks/useGeoBlocking';
import {
  SUDO_CARD_LOOTBOX_TYPE_SUFFIX,
  SUDO_CARD_LOOTBOX_WITH_AMOUNT_TYPE_SUFFIX,
  SUDO_MINT_TICKET_TYPE_SUFFIX,
} from '../utils/utils';
import { useEssential } from './App';
import { OwnedObjects } from './components/Inventory/OwnedObjects';
import { executeApiCall, getCoins } from './utils';
import TokenInput from './components/TokenInput';
import { TOKENS } from './constants';
import { Button } from './components/Base/Button';
import { Token } from './models';

const SReward: React.FC = props => {
  const suiClient = useSuiClient();
  const {
    wallet,
    network,
    consts,
    accountData,
    refreshAccount,
    priceLoading,
    walletStatus,
    ownedSudoObjects,
    refetchOwnedObjects,
  } = useEssential();
  const { data: walletKiosk, refetch: refetchOwnedKiosk } = useOwnedKiosk(
    accountData.address,
  );
  const { data: cardTrackers, refetch: refetchCardTrackers } = useCardTrackers({
    consts,
  });

  const [invalid, setInvalid] = useState('');
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const isBlocked = useGeoBlocking();
  const kioskClient = useKioskClient();
  const [mode, setMode] = useState<'buy' | 'sell'>('sell');
  const [amount, setAmount] = useState<number | null>(null);
  const [token, setToken] = useState<Token>(
    TOKENS.filter(x => x.symbol === 'slp')[0],
  );

  useEffect(() => {
    if (isBlocked === null || priceLoading) {
      setInvalid('Loading');
      return;
    }

    if (!accountData.address) {
      setInvalid('Please connect wallet');
      return;
    }

    setInvalid('');
  }, [
    isBlocked,
    priceLoading,
    accountData.address,
    ownedSudoObjects,
    walletKiosk,
    consts.sudoNft.package,
    cardTrackers,
    refetchCardTrackers,
  ]);

  const depositSlpToPrizePool = () => {
    if (invalid) {
      return;
    }
    setIsSubmitLoading(true);
    executeApiCall(
      async api => {
        const coins = await getCoins(
          accountData.address,
          consts.coins[token.symbol.toLowerCase()].module,
          suiClient,
        );
        const coinObjects = coins.map(e => e.objectId);
        return api.depositToPrizePool(
          token.symbol,
          coinObjects,
          (amount || 0) * 10 ** consts.coins[token.symbol].decimals,
        );
      },
      res => {
        const txnUrl = `https://suivision.xyz/txblock/${res?.digest}`;
        return (
          <a href={txnUrl} target="_blank" rel="noreferrer">
            {' '}
            Success send tx: {res?.digest}{' '}
          </a>
        );
      },
      e => {
        return `Error: ${e.message}`;
      },
      wallet,
      network,
      suiClient,
      [refreshAccount],
    ).finally(() => setIsSubmitLoading(false));
  };

  const withdrawSlpFromPrizePool = () => {
    if (invalid) {
      return;
    }
    setIsSubmitLoading(true);
    executeApiCall(
      async api => {
        return api.withdrawFromSlpPool(
          token.symbol,
          (amount || 0) * 10 ** consts.coins[token.symbol].decimals,
        );
      },
      res => {
        const txnUrl = `https://suivision.xyz/txblock/${res?.digest}`;
        return (
          <a href={txnUrl} target="_blank" rel="noreferrer">
            {' '}
            Success send tx: {res?.digest}{' '}
          </a>
        );
      },
      e => {
        return `Error: ${e.message}`;
      },
      wallet,
      network,
      suiClient,
      [refreshAccount],
    ).finally(() => setIsSubmitLoading(false));
  };

  const depositSlpComponent = (
    <>
      <TokenInput
        token={TOKENS.filter(x => x.symbol === 'slp')[0]}
        titlePrefix={'Pay'}
        amount={amount}
        onChange={setAmount}
        onChangeToken={setToken}
        usdValue={0}
        keepIconPlaceholder={false}
        tokenList={TOKENS.filter(x => x.symbol === 'slp')}
        tokenEditable={mode === 'buy'}
        showBalance={true}
        balance={
          accountData.balance.length
            ? mode === 'buy'
              ? accountData.balance.filter(e => e.symbol === token.symbol)[0]
                  ?.balance || 0
              : accountData.balance.filter(e => e.symbol === 'slp')[0]
                  ?.balance || 0
            : 0
        }
        showMax={true}
        onMax={() => {
          setAmount(
            accountData.balance.filter(e => e.symbol === 'slp')[0].balance,
          );
        }}
      />
      {
        <Button
          onClick={depositSlpToPrizePool}
          className="app-loader-button bg-primary"
        >
          Deposit SLP
        </Button>
      }
      {
        <Button
          onClick={withdrawSlpFromPrizePool}
          className="app-loader-button bg-primary"
        >
          Withdraw SLP
        </Button>
      }
    </>
  );

  return (
    <div className={`section-container py-5 sm:py-10 sm:px-14 px-5 relative`}>
      <div className="text-3xl !mb-5">"S" Rewards</div>
      <Tab.Group vertical defaultIndex={0}>
        <Tab.List>
          <Tab className="tab-title app-tab-button ui-selected:bg-black ui-selected:text-green ui-not-selected:bg-black ui-not-selected:text-white">
            S Rewards
          </Tab>
          <Tab className="tab-title app-tab-button ui-selected:bg-black ui-selected:text-green ui-not-selected:bg-black ui-not-selected:text-white">
            My Lootboxes
          </Tab>
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 w-full mt-10">
              <div className="relative cursor-pointer transition-transform hover:scale-[101%] overflow-hidden">
                <img
                  src={lootboxImage}
                  alt="Lootbox"
                  className="overflow-hidden rounded-xl w-full aspect-square object-cover scale-[1.0]"
                />
              </div>
              <div className="flex flex-col gap-4">
                <div className="bg-primary/50 border-[1px] border-secondary rounded-lg !p-4">
                  <div className="text-lg text-white leading-normal mb-5 last:mb-0">
                    SLP Lootbox
                  </div>
                  <div className="text-md leading-normal mb-5 last:mb-0">
                    Are you ready for the surpirses in the SLP lootbox?
                  </div>
                  <div className="text-md leading-normal mb-5 last:mb-0">
                    Unbox your surprise gift and share it in your social!
                  </div>
                </div>
                {accountData.address ===
                  '0x9b8b59f9f3361f7491571e9d4bf6e6deb6a896b4b83ef42c9cc7aedee30dfe11' && (
                  <div>{depositSlpComponent}</div>
                )}
              </div>
            </div>
          </Tab.Panel>
          <Tab.Panel>
            <div className="mt-10">
              {accountData.address && (
                <OwnedObjects
                  ownedSudoObjects={ownedSudoObjects}
                  types={[
                    `${consts.sudoLootbox.package}::${SUDO_CARD_LOOTBOX_TYPE_SUFFIX}`,
                    `0xd26252db46636d47dba86224f30cddef7d2bfae305795ebb592a6cf15f7ff5b8::${SUDO_CARD_LOOTBOX_WITH_AMOUNT_TYPE_SUFFIX}<${consts.sudoCore.package}::slp::SLP>`,
                  ]}
                ></OwnedObjects>
              )}
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
};

export default SReward;
